import { call, put, takeLatest } from 'redux-saga/effects';
import FileSaver from 'file-saver';
import { PATHS } from 'src/appConfig/paths';
import { Navigator } from 'src/services';
import { Apis } from 'src/services/api';
import { getDateDisplay } from 'src/utils/momentUtils';
import {
  addDailyTimesheetsAsync,
  addWeeklyTimesheetAsync,
  deleteDailyTimesheetsInWeekByIdAsync,
  exportCSVTimesheetsAsync,
  getDailyTimesheetAuditAsync,
  getDailyTimesheetsByWeeklyAsync,
  getTimesheetsAsync,
  getWeeklyTimesheetsByIdAsync,
} from './actions';
import { DeleteDailyTimesheetByIdPayload, TimesheetTableItem } from './types';
import { PayloadAction } from 'typesafe-actions';

function* getTimesheets(api, action) {
  try {
    const res = yield call(api, action.payload);
    if (res?.ok) {
      const timesheets: TimesheetTableItem[] = mapTimesheetResToDataTable(res);
      yield put(getTimesheetsAsync.success({ data: timesheets, total: res.data.total }));
    } else yield put(getTimesheetsAsync.failure(res));
  } catch (err) {
    yield put(getTimesheetsAsync.failure(err));
  }
}

function* exportCSVTimesheets(api, action) {
  try {
    const res = yield call(api, action.payload);
    if (res?.ok) {
      const csvData = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const fileName = `Weekly Timesheet ${action.payload.startDate}-${action.payload.endDate}.csv`;
      FileSaver.saveAs(csvData, fileName);

      yield put(exportCSVTimesheetsAsync.success({}));
    } else yield put(exportCSVTimesheetsAsync.failure(res));
  } catch (err) {
    yield put(exportCSVTimesheetsAsync.failure(err));
  }
}

function* getDailyTimesheetsByWeekly(api, action) {
  try {
    const timesheet = yield call(api, action.payload);
    yield put(getDailyTimesheetsByWeeklyAsync.success(timesheet.data));
  } catch (err) {
    yield put(getDailyTimesheetsByWeeklyAsync.failure(err));
  }
}

function* getWeeklyTimesheetsById(api, action) {
  try {
    const timesheet = yield call(api, action.payload);
    yield put(getWeeklyTimesheetsByIdAsync.success(timesheet.data));
  } catch (err) {
    yield put(getWeeklyTimesheetsByIdAsync.failure(err));
  }
}

function* addWeeklyTimesheet(api, action) {
  try {
    const res = yield call(api, action.payload);
    if (res?.ok) {
      yield put(addWeeklyTimesheetAsync.success(res.data));
      Navigator.navigate(`${PATHS.updateTimesheet}/${res.data.id}`);
    } else yield put(addWeeklyTimesheetAsync.failure(res));
  } catch (err) {
    yield put(addWeeklyTimesheetAsync.failure(err));
  }
}

function* addDailyTimesheets(api, action) {
  try {
    const res = yield call(api, action.payload);
    if (res?.ok) {
      yield put(addDailyTimesheetsAsync.success(res.data));
    } else yield put(addDailyTimesheetsAsync.failure(res));
  } catch (err) {
    yield put(addDailyTimesheetsAsync.failure(err));
  }
}

function* getDailyTimesheetAudit(api, action) {
  try {
    const audit = yield call(api, action.payload.uri);
    if (action.payload.callback) {
      action.payload.callback(audit.data);
    }
    yield put(getDailyTimesheetAuditAsync.success());
  } catch (err) {
    yield put(getDailyTimesheetAuditAsync.failure(err));
  }
}

function* deleteDailyTimesheetsInWeekById(
  api: Apis['deleteDailyTimesheetInWeekById'],
  { payload }: PayloadAction<any, DeleteDailyTimesheetByIdPayload>,
) {
  try {
    const res = yield call(api, payload);
    if (!res.ok) {
      yield put(deleteDailyTimesheetsInWeekByIdAsync.failure(res));
    } else {
      yield put(deleteDailyTimesheetsInWeekByIdAsync.success({ dailyTimesheetId: payload['dailyTimesheetId'] }));
    }
  } catch (err) {
    yield put(deleteDailyTimesheetsInWeekByIdAsync.failure(err));
  }
}

const mapTimesheetResToDataTable = res => {
  return res.data.weeklyTimesheets.map(
    item =>
      ({
        status: item.status,
        trade: item.trade,
        id: item.id,
        overtimeHours: item.overTimeHours,
        level: item.level,
        rate: item.rate,
        employeeId: item.employeeNoId,
        regularHours: Number(item.regularHours),
        employeeLastName: `${item.lastName}, ${item.firstName}`,
        startDate: `${getDateDisplay(item.startDate)} - ${getDateDisplay(item.endDate)}`,
        totalHours: Number(item.regularHours) + Number(item.overTimeHours),
      } as TimesheetTableItem),
  );
};

export default function timesheetSaga(apiInstance: Apis) {
  return [
    takeLatest(getTimesheetsAsync.request, getTimesheets, apiInstance.getTimesheets),
    takeLatest(exportCSVTimesheetsAsync.request, exportCSVTimesheets, apiInstance.exportCSVTimesheets),
    takeLatest(
      getDailyTimesheetsByWeeklyAsync.request,
      getDailyTimesheetsByWeekly,
      apiInstance.getDailyTimesheetsByWeekly,
    ),
    takeLatest(addWeeklyTimesheetAsync.request, addWeeklyTimesheet, apiInstance.addWeeklyTimesheet),
    takeLatest(getWeeklyTimesheetsByIdAsync.request, getWeeklyTimesheetsById, apiInstance.getWeeklyTimesheetsById),
    takeLatest(addDailyTimesheetsAsync.request, addDailyTimesheets, apiInstance.addDailyTimesheets),
    takeLatest(getDailyTimesheetAuditAsync.request, getDailyTimesheetAudit, apiInstance.getDailyTimesheetAudit),
    takeLatest(
      deleteDailyTimesheetsInWeekByIdAsync.request,
      deleteDailyTimesheetsInWeekById,
      apiInstance.deleteDailyTimesheetInWeekById,
    ),
  ];
}
